import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Wallet } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { scrollToEl, scrollToTop } from "../lib/anim";
import { useMobileAuth } from "../context/MobileAuthContext";

const LINKS = [
  { label: "How It Works", href: "#how-it-works" },
  { label: "Why We Pay", href: "#why-we-pay" },
  { label: "Dual-Engine", href: "#dual-engine" },
  { label: "Calculator", href: "#calculator" },
  { label: "FAQ", href: "#faq" },
];

export const Nav = ({ onDownload, onBuy }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { isAuthed } = useMobileAuth();

  const goWithdraw = () => {
    setOpen(false);
    navigate(isAuthed ? "/dashboard" : "/login");
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    if (open) {
      setOpen(false);
      setTimeout(() => scrollToEl(href), 380);
    } else {
      scrollToEl(href);
    }
  };

  return (
    <motion.header
      data-testid="main-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
      className={`fixed top-0 left-0 right-0 z-50 bg-black transition-shadow duration-300 ${
        scrolled ? "border-b border-white/10 shadow-lg shadow-black/40" : "border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          onClick={(e) => {
            e.preventDefault();
            scrollToTop();
          }}
          className="flex items-center group"
        >
          <img
            src="/geonetix-logo-full.webp"
            alt="GeoNetix"
            className="h-10 md:h-12 w-auto object-contain"
          />
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              onClick={() => go(l.href)}
              className="text-sm text-slate-300 hover:text-[#00B4D8] transition-colors duration-200 font-medium"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-buy-dvr-btn"
            onClick={() => go("#buy-dvr")}
            className="hidden sm:inline-flex items-center gap-2 rounded-full border border-[#00B4D8]/50 px-5 py-2.5 text-sm font-semibold text-[#00B4D8] hover:bg-[#00B4D8]/10 transition-colors duration-200"
          >
            Buy Car DVR
          </button>
          <button
            data-testid="nav-withdraw-btn"
            onClick={goWithdraw}
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#4CAF50] px-5 py-2.5 text-sm font-semibold text-[#04140a] hover:brightness-110 transition-[filter,transform] duration-200 hover:-translate-y-0.5 gx-green-glow"
          >
            <Wallet size={15} /> {isAuthed ? "Withdraw" : "Login"}
          </button>
          <button
            data-testid="nav-download-btn"
            onClick={onDownload}
            className="hidden lg:inline-flex items-center rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-slate-100 hover:border-[#4CAF50] hover:text-[#4CAF50] transition-colors duration-200"
          >
            Download App
          </button>
          <button
            data-testid="nav-menu-toggle"
            className="md:hidden text-slate-200 p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden bg-black border-b border-white/10"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-nav-link-${l.href.replace("#", "")}`}
                  onClick={() => go(l.href)}
                  className="text-left py-3 text-slate-200 border-b border-white/5"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-withdraw-btn"
                onClick={goWithdraw}
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-[#4CAF50] px-5 py-3 text-sm font-semibold text-[#04140a]"
              >
                <Wallet size={16} /> {isAuthed ? "Withdraw Earnings" : "Login"}
              </button>
              <button
                data-testid="mobile-buy-dvr-btn"
                onClick={() => go("#buy-dvr")}
                className="mt-2 rounded-full border border-[#00B4D8]/50 px-5 py-3 text-sm font-semibold text-[#00B4D8]"
              >
                Buy Car DVR
              </button>
              <button
                data-testid="mobile-download-btn"
                onClick={onDownload}
                className="mt-2 rounded-full border border-white/20 px-5 py-3 text-sm font-semibold text-slate-100"
              >
                Download App
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
